import React, { useState, useEffect } from "react";
import { 
  Grid, 
  TextField, 
  Typography, 
  Box, 
  IconButton, 
  Divider,
  Paper
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const ImageDropZone = ({ file, onFileSelect, onFileRemove, label, height = "150px" }) => {
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    if (typeof file === "string") {
      setPreview(file);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return (
    <Box
      sx={{
        border: "2px dashed #ccc",
        borderRadius: "8px",
        padding: "8px",
        textAlign: "center",
        position: "relative",
        bgcolor: "#fafafa",
        cursor: "pointer",
        transition: "all 0.3s ease",
        height: height,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        "&:hover": {
          borderColor: "#1976d2",
          bgcolor: "#f4f9ff",
        },
      }}
      component="label"
    >
      {preview ? (
        <>
          <img
            src={preview}
            alt={label}
            style={{
              maxHeight: "100%",
              maxWidth: "100%",
              objectFit: "contain",
              borderRadius: "4px"
            }}
          />
          <IconButton
            size="small"
            onClick={(e) => {
              e.preventDefault();
              onFileRemove();
            }}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              bgcolor: "rgba(255, 255, 255, 0.8)",
              "&:hover": { bgcolor: "rgba(255, 255, 255, 1)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      ) : (
        <>
          <CloudUploadIcon sx={{ fontSize: 30, color: "#9e9e9e", mb: 0.5 }} />
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
        </>
      )}
      <input
        type="file"
        hidden
        accept="image/*"
        onChange={(e) => {
          if (e.target.files && e.target.files[0]) {
            onFileSelect(e.target.files[0]);
          }
        }}
      />
    </Box>
  );
};

const ShopForm = ({ formData, setFormData, errors, isBranch = false }) => {
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "location") {
      // Only allow numbers, minus sign, dot, comma, and space
      const regex = /^[0-9.,\-\s]*$/;
      if (!regex.test(value)) {
        return;
      }
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleFileSelect = (name, file) => {
    setFormData({ ...formData, [name]: file });
  };

  const handleOtherImageSelect = (index, file) => {
    const updatedImages = [...formData.otherImages];
    updatedImages[index] = file;
    setFormData({ ...formData, otherImages: updatedImages });
  };

  const removeFile = (name) => {
    setFormData({ ...formData, [name]: null });
  };

  const removeOtherImage = (index) => {
    const updatedImages = [...formData.otherImages];
    updatedImages[index] = null;
    setFormData({ ...formData, otherImages: updatedImages });
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Grid container spacing={2}>
        {/* Basic Information */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: "bold" }}>
            Basic Information
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Shop Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            error={!!errors.name}
            helperText={errors.name}
            size="small"
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Phone Number"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            error={!!errors.phone}
            helperText={errors.phone}
            size="small"
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="About Us"
            name="aboutUs"
            value={formData.aboutUs}
            onChange={handleInputChange}
            multiline
            rows={2}
            size="small"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address"
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            multiline
            rows={2}
            size="small"
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Location (Latitude, Longitude)"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            error={!!errors.location}
            helperText={errors.location || "e.g. 16.8409, 96.1735"}
            size="small"
          />
        </Grid>

        {!isBranch && (
          <>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: "bold" }}>
                Social Media Links
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Instagram Link"
                name="instagramLink"
                value={formData.instagramLink}
                onChange={handleInputChange}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Website Link"
                name="websiteLink"
                value={formData.websiteLink}
                onChange={handleInputChange}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="TikTok Link"
                name="tiktokkLink"
                value={formData.tiktokkLink}
                onChange={handleInputChange}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Facebook Link"
                name="facebookLink"
                value={formData.facebookLink}
                onChange={handleInputChange}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="YouTube Link"
                name="youtubeLink"
                value={formData.youtubeLink}
                onChange={handleInputChange}
                size="small"
              />
            </Grid>
          </>
        )}

        {isBranch ? (
          <>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: "bold" }}>
                Image
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <ImageDropZone
                label="Image"
                file={formData.image}
                onFileSelect={(file) => handleFileSelect("image", file)}
                onFileRemove={() => removeFile("image")}
                height="120px"
              />
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: "bold" }}>
                Images (Logo & Banner)
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <ImageDropZone
                label="Logo"
                file={formData.logo}
                onFileSelect={(file) => handleFileSelect("logo", file)}
                onFileRemove={() => removeFile("logo")}
                height="120px"
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <ImageDropZone
                label="Banner"
                file={formData.banner}
                onFileSelect={(file) => handleFileSelect("banner", file)}
                onFileRemove={() => removeFile("banner")}
                height="120px"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: "bold" }}>
                Other Images (Max 4)
              </Typography>
            </Grid>
            {[0, 1, 2, 3].map((index) => (
              <Grid item xs={6} sm={3} key={index}>
                <ImageDropZone
                  label={`Image ${index + 1}`}
                  file={formData.otherImages[index]}
                  onFileSelect={(file) => handleOtherImageSelect(index, file)}
                  onFileRemove={() => removeOtherImage(index)}
                  height="100px"
                />
              </Grid>
            ))}
          </>
        )}
      </Grid>
    </Box>
  );
};

export default ShopForm;
